import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Apertavel from '@/components/movimento/Apertavel';
import type { EventoCompras } from '../types';

export interface CapaDoEvento {
  mobile: string | null;
  desktop: string | null;
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// "sáb, 5 dez · 19h30" no horário de Brasília.
function dataCurta(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    const partes = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      weekday: 'short',
      day: 'numeric',
      month: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(d);
    const pegar = (tipo: string) => partes.find((p) => p.type === tipo)?.value ?? '';
    const diaSemana = pegar('weekday').replace('.', '').toLowerCase();
    const dia = pegar('day');
    const mes = Number(pegar('month'));
    const hora = pegar('hour');
    const minuto = pegar('minute');
    const mesCurto = MESES[mes - 1] ?? '';
    return `${diaSemana}, ${dia} ${mesCurto} · ${hora}h${minuto}`;
  } catch {
    return '';
  }
}

function plural(n: number, um: string, varios: string): string {
  return `${n} ${n === 1 ? um : varios}`;
}

interface Acao {
  texto: string;
  /** Linha de ação em azul. */
  cor: string;
}

// O que acontece ao tocar — escrito na própria linha de ação.
function acaoDoEvento(evento: EventoCompras): Acao {
  const fichas = evento.carteira?.fichas ?? 0;
  const pagos = evento.ingressos?.pagos ?? 0;
  const aguardando = evento.ingressos?.aguardando ?? 0;

  if (evento.carteira) {
    const detalhe = fichas > 0 ? ` · ${plural(fichas, 'ficha', 'fichas')} para retirar` : '';
    return { texto: `Abrir carteira${detalhe}`, cor: 'text-primary-600' };
  }
  if (pagos > 0) {
    return { texto: `Ver ingressos · ${plural(pagos, 'ingresso', 'ingressos')}`, cor: 'text-primary-600' };
  }
  if (aguardando > 0) {
    return {
      texto: `Pagar agora · ${plural(aguardando, 'ingresso', 'ingressos')} aguardando`,
      cor: 'text-primary-600',
    };
  }
  return { texto: 'Ver inscrição', cor: 'text-primary-600' };
}

// Botão grande por evento: arte em miniatura (16:9), nome, data curta e a linha
// de ação. Tocar no cartão inteiro abre o destino.
export default function CartaoEvento({
  evento,
  capa,
}: {
  evento: EventoCompras;
  capa?: CapaDoEvento | null;
}) {
  const navigate = useNavigate();
  const [carregou, setCarregou] = useState(false);

  const imagem = capa?.mobile || capa?.desktop || null;
  const data = dataCurta(evento.eventoEm);
  const acao = acaoDoEvento(evento);

  let destino = '/';
  if (evento.carteira) destino = `/carteira/${evento.carteira.codigo}`;
  else if (evento.ingressos?.aguardando && evento.ingressos.pedidoPagar != null) {
    destino = `/pedido/${evento.ingressos.pedidoPagar}`;
  } else if (evento.ingressos?.pedidoVer != null) destino = `/pedido/${evento.ingressos.pedidoVer}`;
  else if (evento.eventoSlug) destino = `/e/${evento.eventoSlug}`;

  return (
    <div className="overflow-hidden rounded-[16px] border border-background-200 bg-background-50">
      <Apertavel
        onClick={() => navigate(destino)}
        className="flex min-h-[88px] w-full cursor-pointer items-center gap-3 p-3 text-left"
      >
        {/* Miniatura 16:9 — reserva o espaço antes de a arte carregar. */}
        <span
          className="relative block w-[104px] shrink-0 overflow-hidden rounded-[10px]"
          style={{ aspectRatio: '16 / 9', background: 'rgba(232,200,119,0.18)' }}
        >
          {!carregou && (
            <span
              className="absolute inset-0 flex items-center justify-center"
              style={{ color: '#B8934A' }}
              aria-hidden
            >
              <i className="ri-calendar-event-line text-[20px]" />
            </span>
          )}
          {imagem && (
            <img
              src={imagem}
              alt={evento.evento}
              title={`${evento.evento} — meus ingressos e fichas`}
              onLoad={() => setCarregou(true)}
              className={`h-full w-full object-cover object-top transition-opacity duration-200 ${
                carregou ? 'opacity-100' : 'opacity-0'
              }`}
            />
          )}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-bold leading-snug text-foreground-950">
            {evento.evento}
          </span>
          {data && <span className="mt-0.5 block text-[12.5px] text-foreground-600">{data}</span>}
          <span className={`mt-1 block truncate text-[13px] font-semibold ${acao.cor}`}>
            {acao.texto}
          </span>
        </span>

        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-500">
          <i className="ri-arrow-right-s-line text-xl" aria-hidden />
        </span>
      </Apertavel>
    </div>
  );
}